import { decodeUserId, isTokenExpired } from 'Helpers/jwt';
import { PROVIDER_STORAGE_KEY } from 'constants/providers';
import { clearEnrollmentProgress } from 'Helpers/enrollment-progress';
import { createContext, useContext, useEffect, useState } from 'react';

const LoginContext = createContext<any>({
  user: null,
  setUser: (user: any) => {},
  logout: () => {},
});

export const useLogin = () => {
  return useContext(LoginContext);
};

export const LoginProvider: any = ({ children }: any) => {
  const [user, saveUser] = useState<any>();

  /**
   * Escribe en localStorage SOLO valores que existen.
   *
   * `localStorage.setItem('user_id', undefined)` no borra la clave: guarda la
   * CADENA "undefined", que es truthy. Eso inutilizaba el rescate del id desde
   * el JWT que hace el efecto de mas abajo (`if (token && !user_id)`), porque la
   * condicion ya no se cumplia, y la app quedaba consultando con "undefined".
   */
  const persist = (key: string, value: any) => {
    if (value === undefined || value === null || value === '') return;
    localStorage.setItem(key, String(value));
  };

  /**
   * Lee una clave tratando como AUSENTE el residuo que dejo el bug anterior.
   *
   * Las sesiones que ya quedaron rotas tienen guardada la cadena "undefined",
   * que es truthy: sin normalizarla, `!user_id` es false y el rescate desde el
   * JWT nunca correria, asi que esas sesiones NO se autorrepararian tras el fix
   * y seguirian pidiendo datos con "undefined".
   */
  const readStored = (key: string): string | null => {
    const value = localStorage.getItem(key);
    if (value === null || value === '' || value === 'undefined' || value === 'null') return null;
    return value;
  };

  const setUser = (user: any) => {
    /**
     * El id sale de `data.user.id` (ver `AuthForm`). Si no viniera, se rescata
     * del JWT en vez de inventarlo: el backend firma `userId` dentro del token
     * (`createToken` en `login.controller.js`), asi que es la fuente fiable.
     *
     * ANTES habia aqui un `user.user_id = 1` silencioso. Cuando el login no
     * traia el id, la app seguia como si el usuario fuera el 1, asi que las
     * consultas se hacian contra OTRO usuario. Ese es el origen del
     * "Request does not exist" en `/payment-setup`: se pedia la solicitud del
     * usuario 1, que no tiene por que ser quien esta logueado.
     */
    const resolvedId =
      user?.user_id ?? (user?.token ? decodeUserId(user.token) : null) ?? undefined;

    persist('token', user?.token);
    persist('user_id', resolvedId);
    persist('role_id', user?.role_id);
    persist('uuid', user?.uuid);
    persist('bar_no', user?.bar_no);
    persist('user', JSON.stringify({ ...user, user_id: resolvedId }));

    saveUser({ ...user, user_id: resolvedId });
  };

  /**
   * Propósito: Cierra la sesión y limpia la eleccion de proveedor del onboarding.
   * Sin esto, el siguiente login en el mismo browser heredaba "configure later"
   * y saltaba a FundingStep aunque no hubiera solicitud.
   */
  const logout = () => {
    const userId = readStored('user_id');
    localStorage.removeItem('token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('uuid');
    localStorage.removeItem('role_id');
    localStorage.removeItem('user');
    localStorage.removeItem('bar_no');
    localStorage.removeItem(PROVIDER_STORAGE_KEY);
    clearEnrollmentProgress(userId);
    /**
     * Output:
     * Side effects — Borra token, ids, eleccion de proveedor y paso del wizard
     */
    saveUser(undefined);
  };

  useEffect(() => {
    const token = readStored('token');
    let user_id = readStored('user_id');
    const uuid = readStored('uuid');
    const role_id = readStored('role_id');

    /**
     * La app Angular del subdominio guarda `token` en el MISMO origen, pero no
     * `user_id`. Sin esto, entrar a `/payment-setup` desde el portal dejaba la
     * sesion a medias (token si, user_id no) y las llamadas a la API fallaban.
     *
     * El `userId` viaja dentro del JWT (el backend lo firma como `userId`, ver
     * `createToken` y `req.user.userId`), asi que se recupera de ahi. La firma la
     * valida el backend en cada peticion.
     */
    if (token && !user_id && !isTokenExpired(token)) {
      const decodedId = decodeUserId(token);
      if (decodedId !== null) {
        user_id = String(decodedId);
        localStorage.setItem('user_id', user_id);
      }
    }

    saveUser((user: any) => {
      return { ...user, token, user_id, uuid, role_id: role_id || '1' };
    });
  }, []);

  return (
    // @ts-ignore
    <LoginContext.Provider value={{ user, setUser, logout }}>{children}</LoginContext.Provider>
  );
};
